
<template>
  <v-app id="vueapp">
    <header class="header">
      <div class="skiplinks hide-for-print">
        <v-btn class="show-on-focus" tabindex="0" @click="focusFirstStep" id="skiplink">
          <div>{{messages.tabFirstStep}}</div>
        </v-btn>
      </div>
      <router-link target="_self" to="/search" v-if="configuration && configuration.showFind">
        <v-btn flat :ripple="false" class="find-booking-btn" id="headerFindButton">
          <v-icon>search</v-icon>
          <div class="find-booking-text">{{messages.findAppointmentButton}}</div>
        </v-btn>
      </router-link>
      <div class="brand" title="logo" role="presentation"></div>
      <!-- Load our language drop down menu component that will be displayed in the header in all views
      Pass needed data from our main Vue App to this component-->
      <language-dropdown
        v-if="selectedLanguage && languages.length > 1"
        :selected-language="selectedLanguage"
        :languages="languages"
        @change-language="changeLanguage"
        id="language_dropdown"
      ></language-dropdown>
    </header>
    <v-container class="main-container" id="main-container">
      <router-view
        v-if="configuration"
        :start-page="startPage"
        :url-params="urlParams"
        :reload-key="reloadKey"
        @increase-reload-key="increaseReloadKey"
        @change-language="changeLanguage"
        :focus-event-key="focusEventKey"
      />
    </v-container>
    <v-footer height="60" class="footer">
      <v-spacer></v-spacer>
      <!--small class="version" v-if="clientVersion">client version: {{clientVersion}}</small-->
      <!--small class="version" v-else>global version: {{version}}</small-->
    </v-footer>
    <v-snackbar
      v-model="showError"
      top
      multi-line
      :timeout="errorDisplayTimeout"
      :color="errorColor"
    >
      {{ errorMessage }}
      <v-btn
        primary-color
        flat
        @click="showError = false"
        v-if="errorMessageCloseButtonVisible"
      >
        <v-icon color="white">cancel</v-icon>
      </v-btn>
    </v-snackbar>
    <!-- v-btn
      @click="executeTest"
    >
        Show Error
    </v-btn -->
  </v-app>
</template>

<script>
import LanguageDropdown from './components/LanguageDropdown'
import RestService from './services/restService'
import DataService from './services/dataService'
import FormatService from './services/formatService'
import { eventHub } from './utils/eventhub'
import Package from '../package.json'
import { mapActions, mapState, mapMutations } from 'vuex'
import { setTimeout } from 'timers'
// use version number in package.json

export default {
  name: 'App',
  components: {
    LanguageDropdown
  },
  mixins: [RestService, DataService, FormatService],
  data: () => ({
    showError: false,
    errorCode: null,
    errorMessage: '',
    errorDisplayTimeout: 6000,
    errorColor: '',
    errorMessageCloseButtonVisible: true,
    languages: [],
    version: null,
    clientVersion: null,
    urlParams: {},
    startPage: 'booking',
    reloadKey: 0,
    sessionLanguage: null,
    focusEventKey: 0
  }),
  computed: {
    ...mapState([
      'configuration',
      'messages',
      'selectedLanguage',
      'funcDebug'
    ])
  },
  methods: {
    ...mapActions([
      'setConfiguration',
      'setMessages',
      'setDefaultCountryPrefix',
      'setSelectedLanguage'
    ]),
    ...mapMutations([
      'setMessagesAgreementHtml1',
      'setMessagesAgreementHtml2',
      'setServiceTemplates'
    ]),
    /* executeTest () {
      eventHub.$emit('show-error', 'I have a lot of things to say men but this is another story!')
    }, */
    showMessage (textData, mode) {
      if (this.funcDebug) console.log('App:method:showMessage')
      // first remove the old Message
      this.showError = false
      // wait a bit for the animation then add the message
      setTimeout(() => {
        switch (mode) {
          case 'info':
            this.errorDisplayTimeout = 6000
            this.errorColor = 'message-info-style'
            this.errorMessageCloseButtonVisible = false
            break
          case 'warning':
            this.errorDisplayTimeout = 6000
            this.errorColor = 'message-warning-style'
            this.errorMessageCloseButtonVisible = false
            break
          case 'error':
            this.errorDisplayTimeout = 10000
            this.errorColor = 'message-error-style'
            this.errorMessageCloseButtonVisible = false
            break
        }
        this.errorMessage = textData
        this.showError = true
      }, 250)
    },
    hideMessage () {
      if (this.funcDebug) console.log('App:method:hideMessage')
      this.showError = false
    },
    /*
    Function that will be triggered when:
    - A user chooses a language in the dropdown menu
    - A user clicks on the appointment link, then the language will be fetched in languageCode in the Appointment object
    The function will then fetch right uiMessages file
    */
    changeLanguage (language) {
      if (this.funcDebug) console.log('App:method:changeLanguage')
      var _this = this
      var thisSelectedLanguage
      if ((language && !language.lang && language !== this.selectedLanguage) || (language.lang && language !== this.selectedLanguage)) {
        if (language && language.lang) {
          thisSelectedLanguage = language
        } else {
        /*
        Since only language code is stored in the Appointment info, we need to loop through our languages array to fetch right language object
        */
          for (var i = 0; i < this.languages.length; i++) {
            var lang = this.languages[i]
            if (lang.lang === language) {
              thisSelectedLanguage = lang
              break
            }
          }
        }
        var composedLanguageCode = thisSelectedLanguage.lang + '_' + thisSelectedLanguage.countryCodeCountry
        composedLanguageCode = composedLanguageCode.toLowerCase()
        this.getMessages(composedLanguageCode).then(response => {
          _this.setMessages(response.data)
          _this.checkRTL()
          _this.replaceAgreementLinks()
          _this.setSelectedLanguage(thisSelectedLanguage)
          _this.sessionLanguage = composedLanguageCode
          // save session language
          localStorage.lang = _this.sessionLanguage
        })
        document.documentElement.setAttribute('lang', thisSelectedLanguage.lang)
      }
    },
    // Will be used when we can recieve entire languages array from the backend, then we won't have to hardcode it in the client
    initLanguage (configLanguages) {
      if (this.funcDebug) console.log('App:method:initLanguage')
      var _this = this
      if (this.funcDebug) console.log('Session language at beginning of init: ' + this.sessionLanguage)
      // have to check if sessionlanguage is in, then setup
      var sessionLanguageIsInConfig = false
      if (this.sessionLanguage) {
        // check if in
        for (var i = 0; i < configLanguages.length; i++) {
          let composedLanguageCode = configLanguages[i].lang + '_' + configLanguages[i].countryCodeCountry
          if (this.sessionLanguage === composedLanguageCode.toLowerCase()) {
            sessionLanguageIsInConfig = true
            break
          }
        }
      }
      for (let i = 0; i < configLanguages.length; i++) {
        var language = configLanguages[i]
        var composedLanguageCode = language.lang + '_' + language.countryCodeCountry
        composedLanguageCode = composedLanguageCode.toLowerCase()
        if (language.countryCode) {
          if (!language.countryCodeCountry) { language.countryCodeCountry = language.countryCode }
          if (!language.countryCodeLang) { language.countryCodeLang = language.countryCode }
        }
        try {
          language.img = require('./assets/flags/' + language.countryCodeLang + '.png')
        } catch {
          language.img = ''
        }
        if (language.flag && language.flag.length > 0) { language.img = language.flag }
        // check if it is the session language
        if (sessionLanguageIsInConfig) {
          if (this.sessionLanguage === composedLanguageCode) {
            this.setSelectedLanguage(language)
            localStorage.lang = this.sessionLanguage
            if (language.countryCodeCountry) {
              this.setDefaultCountryPrefix(language.countryCodeCountry)
            } else {
              this.setDefaultCountryPrefix(language.countryCode)
            }
          }
        } else {
          if (language.default) {
            this.setSelectedLanguage(language)
            this.sessionLanguage = composedLanguageCode
            // save session language
            localStorage.lang = this.sessionLanguage
            if (language.countryCodeCountry) {
              this.setDefaultCountryPrefix(language.countryCodeCountry)
            } else {
              this.setDefaultCountryPrefix(language.countryCode)
            }
          }
        }
        this.languages.push(language)
      }
      this.getMessages(this.selectedLanguage.lang + '_' + this.selectedLanguage.countryCodeCountry).then(response => {
        _this.setMessages(response.data)
        _this.checkRTL()
        _this.replaceAgreementLinks()
      })
    },
    // Initialize rtl if language translation file has set the property direction to rtl
    checkRTL () {
      if (this.funcDebug) console.log('App:method:checkRTL')
      if (this.messages['direction'] === 'rtl') {
        document.documentElement.setAttribute('dir', 'rtl')
        this.$vuetify.rtl = true
      } else {
        document.documentElement.setAttribute('dir', 'ltr')
        this.$vuetify.rtl = false
      }
    },
    focusFirstStep () {
      this.focusEventKey++
    },
    checkUrlParams () {
      if (this.funcDebug) console.log('App:method:checkUrlParams')
      if (this.$route && this.$route.params) {
        this.urlParams = this.$route.params
        this.startPage = this.$route.name
      }
    },
    replaceAgreementLinks () {
      if (this.funcDebug) console.log('App:method:replaceAgreementLinks')
      var agreementHtml1 = this.messages.contactDetailsContentAgreementText1
      if (agreementHtml1.indexOf('<link>') >= 0) {
        agreementHtml1 = agreementHtml1.replace('<link>', '<a href="#">')
        agreementHtml1 = agreementHtml1.replace('</link>', '</a>')
      }
      this.setMessagesAgreementHtml1(agreementHtml1)
      var agreementHtml2 = this.messages.contactDetailsContentAgreementText2
      if (agreementHtml2.indexOf('<link>') >= 0) {
        agreementHtml2 = agreementHtml2.replace('<link>', '<a href="#">')
        agreementHtml2 = agreementHtml2.replace('</link>', '</a>')
      }
      this.setMessagesAgreementHtml2(agreementHtml2)
    },
    increaseReloadKey () {
      if (this.funcDebug) console.log('App:method:increaseReloadKey')
      this.reloadKey += 1
    },
    showErrorMessage (error) {
      if (this.funcDebug) console.log('App:methods:showErrorMessage')
      this.errorMessage = 'A network error has occurred. Please reload the page. If that did not help please contact us directly.'
      if (error.response && error.response.status === 500) {
        // Default error in English only if translation unavailable.
        // Language Translation
        if (error.response.request.responseText.includes('ESOCKETTIMEDOUT') && this.messages && this.messages.errorMessageConnectionNotConnected) {
          this.errorMessage = this.messages.errorMessageConnectionNotConnected
        } else if (error.response.request.responseText.includes('ECONNREFUSED') && this.messages && this.messages.errorMessageConnectionRefused) {
          this.errorMessage = this.messages.errorMessageConnectionRefused
        } else if (error.response.request.responseText.includes('ECONNRESET') && this.messages && this.messages.errorMessageConnectionReset) {
          this.errorMessage = this.messages.errorMessageConnectionReset
        } else if (this.messages.errorMessageNetworkError) {
          this.errorMessage = this.messages.errorMessageNetworkError
        }
        this.showMessage(this.errorMessage, 'error')
      } else if (error.response && [501, 502, 503, 504, 505, 506, 507, 508, 511].includes(error.response.status) && error.response.statusText) {
        // TODO add specific messages here on client requeust
        this.showMessage(error.response.statusText, 'error')
      } else if (!error.response && error.request.readyState === 4) {
        if (this.messages && this.messages.errorMessageNetworkError) {
          this.errorMessage = this.messages.errorMessageNetworkError
        }
        this.showMessage(this.errorMessage, 'error')
      }
      // All http 400 status errors should be handled locally and NOT here
    },
    reloadConfiguration (routingObj) {
      if (this.funcDebug) console.log('App:methods:reloadConfiguration')
      this.getConfiguration().then(response => {
        if (this.funcDebug) console.log('App:methods:reloadConfiguration:getConfiguration.then')
        // Assign data to our variable that have been declared in our Vue App data object.
        let loadedConfiguration = this.convertObject(response.data)
        this.setConfiguration(loadedConfiguration)
        this.$router.push(routingObj)
      })
    }
  },
  mounted () {
    if (this.funcDebug) console.log('App:mounted')
    // When our main Vue is loaded, we need to fetch some data that needs to be used in all components
    // Here we need to fetch Configuration from the server
    var _this = this
    this.version = Package.version
    if (Package['client-version']) {
      this.clientVersion = Package['client-version']
    }
    if (this.clientVersion) {
      console.log('Qmatic Web Booking - version ' + this.clientVersion)
    } else {
      console.log('Qmatic Web Booking - version ' + this.version)
    }
    // load config from AdminUI
    this.getConfiguration().then(response => {
      // Assign data to our variable that have been declared in our Vue App data object.
      var loadedConfiguration = _this.convertObject(response.data)
      _this.setConfiguration(loadedConfiguration)
      // console.log('this.configuration: ', this.configuration)
      // set language
      if (_this.configuration.qmaticWebBookingLanguages) {
        if (_this.funcDebug) console.log('Language from Query: ' + _this.$route.query.lang)
        _this.checkUrlParams()
        // load from session, if present then use it
        _this.sessionLanguage = null
        if (_this.$route.query.lang) {
          // url config found, use it
          var myQueryLang = _this.$route.query.lang
          if (myQueryLang) {
            myQueryLang = myQueryLang.toLowerCase()
            _this.sessionLanguage = myQueryLang
          }
        } else if (localStorage.lang) {
          _this.sessionLanguage = localStorage.lang
        }
        _this.initLanguage(_this.configuration.qmaticWebBookingLanguages)
      }
    })
    this.getServiceTemplates().then(response => {
      // save the templates in the store
      _this.setServiceTemplates(response.data)
    })
  },
  created () {
    if (this.funcDebug) console.log('App:created')
    eventHub.$on('show-warning', (textData) => this.showMessage(textData, 'warning'))
    eventHub.$on('show-error', (textData) => this.showMessage(textData, 'error'))
    eventHub.$on('show-info', (textData) => this.showMessage(textData, 'info'))
    eventHub.$on('hide-message', () => this.hideMessage())
    eventHub.$on('reload-configuration', (routingObj) => this.reloadConfiguration(routingObj))
    eventHub.$on('show-error-message', (error) => this.showErrorMessage(error))
  },
  updated () {
    if (this.funcDebug) console.log('App:updated')
    /* if (this.error && this.errorCode !== 500) {
      this.error = false
    } */
  },
  beforeDestroy () {
    if (this.funcDebug) console.log('App:beforeDestroy')
    eventHub.$off('show-warning')
    eventHub.$off('show-error')
    eventHub.$off('show-info')
    eventHub.$off('hide-message')
    eventHub.$off('reload-configuration')
  }
}
</script>
<style lang="scss">
@import "./assets/fonts/Roboto-material-icons.css";

body {
  margin: 0;
  font-weight: 400;
  color: var(--default-font-color) !important;
  font-family: CustomQWBFont, Roboto, Arial, sans-serif;
  font-size: 16px;
  height: 100vh;
  padding: 0;
  & .primary--text {
    color: var(--input-label-text-color-focus) !important;
  }
  & .accent--text {
    color: var(--focused-element) !important;
    caret-color: var(--focused-element) !important;
  }
}

.relative {
  position: relative;
}
.screen-reader-only {
  position: absolute;
  height: 1px;
  width: 1px;
  clip: rect(1px 1px 1px 1px);
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: polygon(0px 0px, 0px 0px, 0px 0px);
  -webkit-clip-path: polygon(0px 0px, 0px 0px, 0px 0px);
  overflow: hidden !important;
}

.skiplinks button {
    margin: 18px;
    padding: 10px;
    display: inline-block;
    background-color: var(--primary-btn-bg-color) !important;
    color: var(--primary-btn-text-color) !important;
    text-decoration: underline;
    z-index: 1;
    font-size: 18px;
    z-index: 9999 !important;
    max-width: 50%;
    justify-content: center;
    text-transform: uppercase;
    font-size: 16px;
}

.show-on-focus {
    position: absolute !important;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0,0,0,0);
    top:1px;
}

.show-on-focus:focus {
  position: relative !important;
  width: auto !important;
  height: auto !important;
  color: var(--primary-btn-text-color) !important;
  left: 0px;
  text-decoration: underline;
}

.skiplinks button.show-on-focus:hover{
  background-color: var(--defined-focused-element) !important;
  color: var(--btn-text-color) !important;
}

.header {
  background-color: var(--header-bg-color) !important;
  border-bottom: 3px solid;
  border-color: var(--header-border-bottom-color) !important;
  -ms-flex-negative: 0;
  flex-shrink: 0;
  height: 85px;
  width: 100%;
  display: var(--display-header);
}
.find-booking-btn.v-btn.v-btn--flat {
  position: absolute;
  top: 20px;
  left: 0;
  height: 43px;
  font-weight: 400;
  font-size: 14px;
  margin: 0 0 0 16px;
  color: var(--primary-btn-text-color) !important;
  background-color: var(--primary-btn-bg-color) !important;

  @at-root [dir="rtl"] & {
    left: auto;
    right: 0;
    margin: 0 16px 0 0;
  }
}

.reset-btn.v-btn--active:before, .v-btn:hover:before, .v-btn:focus:before{
  background-color: initial !important;
}

.header a:link {
  border-bottom: none;
}

.header a:hover {
  color: var(--focused-element) !important;
}

.brand {
  display: block;
  height: 54px;
  margin: 0 auto;
  width: 290px;
  position: absolute;
  top: 13px;
  left: 0;
  right: 0;
  background: var(--logo-path) no-repeat center;
}

#vueapp .container.main-container {
  max-width: 900px;
  display: block;
  margin: 0 auto;
}
#vueapp .footer {
  background: transparent;
}
.footer .version {
  padding-right: 16px;
}

#vueapp.theme--light.application {
  background: linear-gradient(180deg, var(--main-bg-gradient-top-color) 0%, var(--main-bg-gradient-bottom-color) 100%) !important;
}

.input-btn.v-btn {
  background-color: var(--primary-btn-bg-color) !important;
  color: var(--primary-btn-text-color) !important;
  border-radius: 0;
}

.wrapper {
  /*border: 2px solid;
  border-color: var(--primary-border-color) !important;*/
  background-color: var(--wrapper-bg-color-primary) !important;
}

h1 {
  text-align: center;
  padding-top: 24px;
  color: var(--default-font-color);
}

h2 {
  font-weight: 400;
  color: var(--primary-bg-text-color);
}

.progress-bar{
  color: var(--progress-bar-color);
}

.info-text {
  font-size: 18px;
  font-weight: 300;
  padding: 16px 24px;
  margin-bottom:5px;
  color: var(--default-font-color);

  &.agreement {
    margin: 0;
  }

  @at-root [dir="rtl"] & {
    text-align: right;
  }
}

.v-date-picker-header__value > div{
  margin:5px 0;
}

.v-btn.v-btn--flat.theme--light:hover,
.v-btn.v-btn--icon.theme--light:hover,
.phone-verification-btn.v-btn.v-btn--flat.theme--light:hover,
.submit.v-btn.v-btn--flat.theme--light:hover,
.submit.back.v-btn.v-btn--flat.theme--light:hover,
.cancel.v-btn.v-btn--flat.theme--light:hover,
.edit.v-btn.v-btn--flat.theme--light:hover,
.reschedule .submit.v-btn.v-btn--flat.theme--light:hover,
.v-btn.v-btn--absolute.v-btn--icon.v-btn--right.theme--light.error:hover,
.reset-btn.v-btn.v-btn--icon.theme--light.error:hover {
  background-color: var(--defined-focused-element) !important;
  color: var(--primary-btn-text-color) !important;
}

.submit.v-btn.v-btn--flat.theme--light {
  height: 64px;
  background-color: var(--primary-btn-bg-color) !important;
  color: var(--primary-btn-text-color) !important;
  font-weight: 400;
  font-size: 17px;
  min-width: 300px;
  border-radius: 0;
}

.submit.v-btn.v-btn--disabled.v-btn--flat.theme--light,
.phone-verification-btn.v-btn.v-btn--disabled.v-btn--flat.theme--light,
.send-verification-btn.v-btn.v-btn--disabled.v-btn--flat.theme--light {
  background-color: var(--primary-btn-bg-disabled-color) !important;
}

.confirmation-text {
  padding-bottom: 16px;
}

.info-text.appointment-details {
  padding: 24px;
}

.content .wrapper h1 {
  padding: 20px;
}

.primary-color {
  background-color: var(--primary-color) !important;
  color: var(--primary-bg-text-color) !important;
}

.submit.back.v-btn.v-btn--flat.theme--light {
  display: block;
  margin: 36px 0 0;
}

a:link {
  border-bottom: 1px solid #9199aa;
  text-decoration: none;
}

a,
a:active,
a:link,
a:visited {
  color: #2a5bb4;
  outline: none;
}

a:focus {
  outline-color: blue !important;
  outline: solid;
}

a:hover {
  color: var(--focused-element) !important;
}

div:not(#main-container):focus, button:not(#dobPickerIcon):focus, a:focus, li:focus, h1:focus,
  input:not(#customFields1):not(#customFields2):not(#customFields3):not(#customFields4):not(#customFields5):not(#customFields6):not(#customFields7):not(#customFields8):not(#customFields9):not(#customFields10):not(#verification-code):not(#LastName):not(#FirstName):not(#Email):not(#Notes):not(#countryCode):not(#Phone):not(#DOB):not(#ConfirmPhone):not(#ConfirmEmail):not(#AppRef):not(#CustRef):focus  {
  outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
  opacity:1 !important;
  box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
  // box-shadow: var(--tabfocus-shadow) !important;
}

.v-text-field--outline.v-input--is-focused
  .v-input__slot
  .selected-language-name {
  text-decoration: underline;
}

.v-text-field--outline.v-input--is-focused .v-input__slot{
    border: var(--tabfocus-outline-color) !important;
    outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
    opacity:1 !important;
    box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    -webkit-box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    -moz-box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
  }

.info-text.agreement .v-input__slot{
    align-items: normal;
  }

.v-text-field--outline.v-input--is-focused .v-input__slot {
  border: 2px solid;
}

.theme--light.v-list .v-list__tile--highlighted {
  background-color: var(--focused-element) !important;
  color: var(--primary-bg-text-color) !important;
}

.theme--light.v-list .v-list__tile--highlighted .language-option {
  color: var(--primary-bg-text-color) !important;
}

.dialog-title {
  background-color: var(--primary-color) !important;
  color: var(--primary-bg-text-color) !important;
}

.edit.v-btn.v-btn--flat.theme--light {
  background-color: var(--primary-btn-bg-color) !important;
}

.cancel.v-btn.v-btn--flat.theme--light {
  background-color: var(--primary-btn-bg-color) !important;
}

.cancel.v-btn.v-btn--flat.theme--light,
.edit.v-btn.v-btn--flat.theme--light {
  color: var(--primary-btn-text-color) !important;
  font-weight: 400;
  height: 56px;
}

.grecaptcha-badge {
  position: fixed;
  bottom: 0;
  right: 0;
}

.v-text-field.v-text-field--solo .v-input__control {
  min-height: 56px;
}
.input-field.v-text-field--outline.v-input--is-disabled .v-input__slot {
  border-color: #d9d9d9;
  background: #616161;
}

.confirmation-service-adult{
  display: inline-block;
}

@media screen and (max-width: 600px) {
  h1 {
    font-size: 27px;
    padding: 16px;
  }

  .container{
    padding:0px;
  }

  .booking-container{
    padding:0px !important;
  }

  @media only screen and (max-width: 359px) and (min-width: 300px) {
    h1 {
      font-size: 24px;
    }

    .submit.v-btn.v-btn--flat.theme--light {
      min-width: 250px;
    }
  }
  .find-booking-btn.v-btn.v-btn--flat {
    min-width: 70px;
  }
  .find-booking-btn .find-booking-text {
    display: none;
  }

  h2 {
    font-size: 25px;
    font-weight: 400;
  }

  .brand {
    max-height: 50px;
    width: 100px;
    background-size: contain;
    -webkit-background-size: contain;
  }

  .brand img {
    height: 44px;
    width: 100px;
    display: block;
    margin: auto;
  }

  .header {
    height: 71px;
  }

  .show-on-focus:focus{
    max-width: 100% !important;
    text-align: center !important;
    margin: 0 !important;
    width: 100% !important;
    left: 0 !important;
    top: 3px !important;
    height: auto !important;
    z-index: 9999;
    padding: 22px 10px;
  }

  #vueapp {
    padding-top: 0;
  }
  .submit.v-btn.v-btn--flat.theme--light,
  .submit.back.v-btn.v-btn--flat.theme--light {
    width: auto;
    font-size: 16px;
    margin: 16px auto 0 auto;
  }

  .submit.back.v-btn.v-btn--flat.theme--light {
    width: 100%;
  }

  .submit.v-btn.v-btn--flat.theme--light .v-btn__content {
    white-space: normal;
  }

  .send-verification-btn.v-btn{
    width:48%;
    padding: 0 8px;
    overflow: hidden;
  }

  .send-verification-btn.v-btn .v-btn__content{
    white-space: normal;
    line-height: 18px;
  }

  .info-text {
    font-size: 18px;
    font-weight: 300;
    padding: 16px;
  }

  .content .wrapper h1 {
    padding: 16px 0;
  }

}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  /* IE10+ CSS styles go here */
  * {
    -o-transition-property: none !important;
    -moz-transition-property: none !important;
    -ms-transition-property: none !important;
    -webkit-transition-property: none !important;
    transition-property: none !important;
    -o-transform: none !important;
    -moz-transform: none !important;
    -ms-transform: none !important;
    -webkit-transform: none !important;
    -webkit-transform-origin: none !important;
    transform-origin: none !important;
    transform: none !important;
    -webkit-animation: none !important;
    -moz-animation: none !important;
    -o-animation: none !important;
    -ms-animation: none !important;
    animation: none !important;
  }

  .v-input.v-input--selection-controls.v-input--checkbox.v-input--is-focused
    .v-input--selection-controls__input,
  .v-radio.v-radio--is-focused .v-input--selection-controls__input {
    background-color: #c0c0c0;
    border-radius: 50%;
  }
  .v-input.input-field.v-text-field.v-input--is-focused label,
  .v-input.v-text-field--box.v-text-field--enclosed.v-input--is-focused label,
  .v-input.v-text-field--box.v-text-field--enclosed.v-input--is-disabled label,
  .v-input.v-text-field--enclosed.v-text-field--outline.v-input--is-disabled
    label,
  .v-input.v-text-field--enclosed.v-text-field--outline.v-input--is-focused
    label,
  .v-input.input-field.v-text-field.v-input--is-label-active label {
    font-size: 12px;
    top: 0;
  }
  #vueapp .application--wrap {
    display: block;
  }
  #vueapp .footer {
    width: 100%;
    position: absolute;
    bottom: 0;
  }
}

.message-error-style, .message-warning-style, .message-info-style {
  z-index: 999;
  font-size: 18px;
  font-weight: 300;
}

.message-error-style {
  background-color: var(--message-error-bg-color);
}

.message-warning-style {
  background-color: var(--message-warning-bg-color);
}

.message-info-style {
  background-color: var(--message-info-bg-color);
}

.wrapper .info {
  background-color: var(--message-info-bg-color) !important;
}

.container .warning{
  background-color: var(--message-warning-bg-color) !important;
}

// border text field
.v-text-field--outline > .v-input__control > .v-input__slot,
.theme--light.v-text-field--outline > .v-input__control > .v-input__slot {
  border: 1px solid var(--input-border-color) !important;
  background: white !important;
  border-radius: 0;
}

// changed collar around text fields, focused-border class needs to be assigned to them
.v-input--is-focused.focused-border > .v-input__control > .v-input__slot {
  border: var(--tabfocus-outline-color) !important;
  outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
  opacity:1 !important;
  box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
}

// apply custom font
.application {
  font-family: 'CustomQWBFont', 'Roboto', sans-serif;
}

input {
  font-family: 'Roboto', sans-serif;
}

// add some space on regions expansion panels
.region-expansion-panel {
  margin-bottom: 2px;
}

.v-snack--multi-line .v-snack__content {
  height: auto !important;
}

</style>
